/**
 * File: Solutions.jsx
 * Path: /src/components/home/Solutions.jsx
 * Description: Interactive tab section displaying different legal AI solutions with advanced visualization.
 * Key Features: Tabbed interface with animations between content sections and interactive AI visualization.
 * Maintenance Notes: Update when adding new solutions or changing solution details.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import VeritectAIVisualization from './VeritectAIVisualization';

const Solutions = () => {
  const [activeTab, setActiveTab] = useState('research');
  const [sectionRef, isVisible] = useIntersectionObserver({ 
    threshold: 0.1,
    triggerOnce: true
  });

  const solutions = {
    research: {
      title: "Legal Research AI",
      description: "Our AI-powered research platform analyzes millions of cases, statutes, and legal documents in seconds to find the most relevant precedents and arguments for your case.",
      features: [
        "Natural language queries for complex legal concepts",
        "Automated brief and memo generation",
        "Citation validation and verification",
        "Jurisdiction-specific filtering and analysis",
        "Integration with leading legal databases"
      ],
      image: "feature-1.svg",
      link: "/solutions/legal-research"
    },
    contract: {
      title: "Contract Analysis",
      description: "Scan and analyze contracts of any length and complexity to identify key terms, risks, obligations, and opportunities with unparalleled accuracy and speed.",
      features: [
        "Risk identification and highlighting",
        "Clause extraction and comparison",
        "Custom clause library and playbooks",
        "Multi-language contract support",
        "Redlining and revision tracking"
      ],
      image: "feature-2.svg",
      link: "/solutions/contract-analysis"
    },
    predict: {
      title: "Judgment Prediction",
      description: "Leverage our advanced machine learning algorithms to predict case outcomes, likely settlement ranges, and judicial tendencies based on historical data.",
      features: [
        "Case outcome probability assessment",
        "Judge-specific ruling analytics",
        "Settlement range predictions",
        "Timeline forecasting",
        "Strategy optimization recommendations"
      ],
      image: "feature-3.svg",
      link: "/solutions/judgment-prediction"
    }
  };

  const currentSolution = solutions[activeTab];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-dark-50"
    >
      <div className="container-padding mx-auto">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-primary-600 font-semibold">Our Solutions</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
            Powerful Tools for Modern Legal Practice
          </h2>
          <p className="text-dark-500 text-lg">
            Explore our suite of AI-powered solutions designed specifically for law firms 
            looking to enhance efficiency and gain competitive advantage.
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div 
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {Object.keys(solutions).map((key) => (
            <Button
              key={key}
              variant={activeTab === key ? "primary" : "ghost"}
              onClick={() => setActiveTab(key)}
              className={`rounded-full ${activeTab === key ? 'shadow-md' : 'shadow-none'}`}
            >
              {solutions[key].title}
            </Button>
          ))}
        </motion.div>

        {/* Content */}
        <div className="max-w-6xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div 
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            >
              {/* Text Content */}
              <div>
                <h3 className="text-2xl font-bold mb-4 gradient-text">
                  {currentSolution.title}
                </h3>
                <p className="text-dark-600 mb-4">
                  {currentSolution.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {currentSolution.features.map((feature, index) => (
                    <motion.li 
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                      className="flex items-start"
                    >
                      <svg className="h-6 w-6 text-primary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </ul>
                <Button 
                  variant="primary"
                  to={currentSolution.link}
                >
                  Learn More
                </Button>
              </div>

              {/* Interactive AI Visualization */}
              <div className="flex justify-center">
                <div className="relative w-full max-w-md">
                  {/* Background decorations */}
                  <div className="absolute inset-0 -z-10">
                    <div className="absolute w-40 h-40 rounded-full bg-primary-100 right-0 bottom-0 -z-10"></div>
                    <div className="absolute w-32 h-32 rounded-full bg-secondary-100 left-10 top-10 -z-10"></div>
                    <div className="absolute w-24 h-24 rounded-full bg-accent-100 right-20 top-5 -z-10"></div>
                  </div>
                  
                  {/* Interactive AI Visualization Component */}
                  <VeritectAIVisualization activeTab={activeTab} />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Solutions;